import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { getSubscriptionStatus, type SubscriptionStatus, type EffectivePlanSource } from './stripe'
import { parseSubscriptionError, logSubscriptionError } from './subscription-errors'
import { ApiRequestError } from './api'
import { type PlanId } from './pricing'

/** 401/403 mean the cached plan may no longer be valid for this session/tenant (logged out
 *  elsewhere, access revoked) - unlike a transient network/server failure, it must not be
 *  preserved, or the UI could keep unlocking a paid plan the user no longer has access to. */
function isAuthOrPermissionFailure(err: unknown): boolean {
    return err instanceof ApiRequestError && (err.statusCode === 401 || err.statusCode === 403)
}

export interface SubscriptionState {
    // State
    subscription: SubscriptionStatus | null
    isLoading: boolean
    error: Error | null
    lastFetched: number | null
    /** True after a 401/403 cleared the cached subscription. Distinct from `subscription`
     *  being null on first load: it tells consumers this session/tenant's access was
     *  actively invalidated, so they must not fall back to some other cached plan (e.g.
     *  AuthContext's, which may not have caught up yet) while this is true. */
    isSessionInvalid: boolean

    // Computed
    /** True only when there is a real, currently active/trialing Stripe or Hotmart subscription. */
    hasActiveSubscription: boolean
    /** The tenant's effective PinDay plan - always resolved, regardless of billing state. */
    currentPlan: PlanId
    effectivePlanSource: EffectivePlanSource | undefined
    hasStripeCustomer: boolean
    isInTrial: boolean
    daysUntilRenewal: number | null

    // Actions
    fetchSubscription: () => Promise<void>
    refreshSubscription: () => Promise<void>
    updateOptimistic: (updates: Partial<SubscriptionStatus>) => void
    clearError: () => void
    reset: () => void
}

/** Fields derived from a SubscriptionStatus, kept in one place so `subscription` and its
 *  derived top-level fields can never drift apart - including across persisted-store hydration. */
type DerivedSubscriptionFields = Pick<
    SubscriptionState,
    'hasActiveSubscription' | 'currentPlan' | 'effectivePlanSource' | 'hasStripeCustomer' | 'isInTrial' | 'daysUntilRenewal'
>

function deriveFields(status: SubscriptionStatus | null): DerivedSubscriptionFields {
    return {
        hasActiveSubscription:
            Boolean(status?.hasActiveBillingSubscription) &&
            (status?.status === 'active' || status?.status === 'trialing'),
        currentPlan: status?.planId || 'free',
        effectivePlanSource: status?.effectivePlanSource,
        hasStripeCustomer: status?.hasStripeCustomer ?? false,
        isInTrial: status?.isInTrial || false,
        daysUntilRenewal: status?.daysUntilRenewal || null,
    }
}

const initialState = {
    subscription: null,
    isLoading: false,
    error: null,
    lastFetched: null,
    isSessionInvalid: false,
    ...deriveFields(null),
}

/**
 * Zustand store for subscription state management
 * Provides global access to subscription data with caching and optimistic updates
 */
export const useSubscriptionStore = create<SubscriptionState>()(
    devtools(
        persist(
            (set, get) => ({
                ...initialState,

                fetchSubscription: async () => {
                    const { lastFetched } = get()

                    // Cache for 1 minute
                    if (lastFetched && Date.now() - lastFetched < 60000) {
                        return
                    }

                    set({ isLoading: true, error: null })

                    try {
                        const status = await getSubscriptionStatus()

                        set({
                            subscription: status,
                            isLoading: false,
                            lastFetched: Date.now(),
                            isSessionInvalid: false,
                            ...deriveFields(status),
                        })
                    } catch (err) {
                        const subscriptionError = parseSubscriptionError(err)
                        logSubscriptionError(subscriptionError, { context: 'subscriptionStore' })

                        if (isAuthOrPermissionFailure(err)) {
                            // Session expired or tenant access revoked: the cached plan is no
                            // longer trustworthy, so clear it rather than let the UI keep
                            // unlocking a plan the user may no longer have access to.
                            set({
                                subscription: null,
                                ...deriveFields(null),
                                error: subscriptionError,
                                isLoading: false,
                                lastFetched: null,
                                isSessionInvalid: true,
                            })
                        } else {
                            // Transient/retryable failure: deliberately leave `subscription`/
                            // derived fields/`lastFetched` untouched. It must not be cached as a
                            // confirmed free/no-customer state, and the next mount should retry
                            // rather than wait out the cache window.
                            set({
                                error: subscriptionError,
                                isLoading: false,
                            })
                        }
                    }
                },

                refreshSubscription: async () => {
                    // Force refresh regardless of cache
                    set({ lastFetched: null })
                    await get().fetchSubscription()
                },

                updateOptimistic: (updates: Partial<SubscriptionStatus>) => {
                    const { subscription } = get()

                    if (!subscription) return

                    const updatedSubscription = { ...subscription, ...updates }

                    set({
                        subscription: updatedSubscription,
                        isSessionInvalid: false,
                        ...deriveFields(updatedSubscription),
                    })
                },

                clearError: () => set({ error: null }),

                reset: () => set(initialState),
            }),
            {
                name: 'subscription-storage',
                partialize: (state) => ({
                    // Persist `subscription` alongside its derived fields (rather than recomputing
                    // them separately) so a page reload within the cache window rehydrates a
                    // consistent state instead of resetting the derived fields to their defaults
                    // while `fetchSubscription` skips refetching.
                    subscription: state.subscription,
                    lastFetched: state.lastFetched,
                    hasActiveSubscription: state.hasActiveSubscription,
                    currentPlan: state.currentPlan,
                    effectivePlanSource: state.effectivePlanSource,
                    hasStripeCustomer: state.hasStripeCustomer,
                    isInTrial: state.isInTrial,
                    daysUntilRenewal: state.daysUntilRenewal,
                }),
                onRehydrateStorage: () => (state) => {
                    if (!state) return
                    // Recompute derived fields from whatever `subscription` was actually persisted,
                    // rather than trusting the persisted derived fields as-is. This self-heals
                    // localStorage payloads written before these fields existed (subscription/
                    // lastFetched present, derived fields silently absent/defaulted) - without it,
                    // such users would rehydrate with currentPlan stuck at 'free' while lastFetched
                    // still blocks fetchSubscription() from refetching for up to a minute.
                    Object.assign(state, deriveFields(state.subscription))
                },
            }
        ),
        {
            name: 'SubscriptionStore',
        }
    )
)

/**
 * Selector hooks for common use cases
 */
export const useHasActiveSubscription = () =>
    useSubscriptionStore((state) => state.hasActiveSubscription)

export const useCurrentPlan = () =>
    useSubscriptionStore((state) => state.currentPlan)

export const useIsInTrial = () =>
    useSubscriptionStore((state) => state.isInTrial)

export const useSubscriptionLoading = () =>
    useSubscriptionStore((state) => state.isLoading)

export const useSubscriptionError = () =>
    useSubscriptionStore((state) => state.error)
